import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import type { AlertResponse, Schema } from "@bw/shared/types/generated";
import { formatDateTime } from "@bw/shared/utils";

type AdminRosterResponse = Schema["PlatformAdminAccessRosterResponse"];
export type Severity = AlertResponse["severity"];
export type Status = AlertResponse["status"];

const STATUS_FILTERS: ReadonlyArray<{
  label: string;
  value: Status | "all";
}> = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "Acknowledged", value: "acknowledged" },
  { label: "Muted", value: "muted" },
];

const SEVERITY_FILTERS: ReadonlyArray<{
  label: string;
  value: Severity | "all";
}> = [
  { label: "All", value: "all" },
  { label: "Critical", value: "critical" },
  { label: "Warning", value: "warning" },
  { label: "Info", value: "info" },
];

const SEVERITY_COLOR: Record<Severity, "error" | "info" | "warning"> = {
  critical: "error",
  info: "info",
  warning: "warning",
};

function safeEmail(value: string | null | undefined): string | null {
  const email = value?.trim() ?? "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function resolveAssigneeName(
  uid: string | null | undefined,
  roster: AdminRosterResponse | undefined,
): string | null {
  if (!uid) return null;
  const candidate = roster?.items.find(
    (item) => item.firebase_uid?.trim() === uid,
  );
  return candidate?.display_name?.trim() || safeEmail(candidate?.email);
}

function humanizeAlertType(value: string): string {
  return value
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function AlertFilterBar({
  locked,
  severity,
  status,
  onSeverityChange,
  onStatusChange,
}: {
  locked: boolean;
  severity: Severity | "all";
  status: Status | "all";
  onSeverityChange: (value: Severity | "all") => void;
  onStatusChange: (value: Status | "all") => void;
}) {
  return (
    <Paper
      component="section"
      aria-label="Alert filters"
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 } }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        divider={<Divider flexItem orientation="vertical" />}
        spacing={2}
        sx={{ alignItems: { lg: "center" } }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" } }}
        >
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ fontWeight: 700, minWidth: 52 }}
          >
            Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Alert status"
            value={status}
            disabled={locked}
            onChange={(_event, value: Status | "all" | null) => {
              if (value) onStatusChange(value);
            }}
          >
            {STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" } }}
        >
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ fontWeight: 700, minWidth: 52 }}
          >
            Severity
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Alert severity"
            value={severity}
            disabled={locked}
            onChange={(_event, value: Severity | "all" | null) => {
              if (value) onSeverityChange(value);
            }}
          >
            {SEVERITY_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function AlertQueue({
  alerts,
  busy,
  filtered,
  locked,
  roster,
  total,
  onAcknowledge,
  onAssign,
  onMute,
  onUnmute,
}: {
  alerts: AlertResponse[];
  busy: boolean;
  filtered: boolean;
  locked: boolean;
  roster: AdminRosterResponse | undefined;
  total: number | undefined;
  onAcknowledge: (alert: AlertResponse) => void;
  onAssign: (alert: AlertResponse) => void;
  onMute: (alert: AlertResponse) => void;
  onUnmute: (alert: AlertResponse) => void;
}) {
  if (alerts.length === 0) {
    return (
      <Paper
        component="section"
        aria-busy={busy || undefined}
        aria-label="Alert queue"
        variant="outlined"
        sx={{ py: 6, px: 2, textAlign: "center" }}
      >
        <Typography color="text.secondary" role="status">
          {filtered
            ? "No alerts match these filters."
            : "No alerts in this view."}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Paper
        component="section"
        aria-busy={busy || undefined}
        aria-label="Alert queue"
        variant="outlined"
        sx={{ overflow: "hidden" }}
      >
        <List disablePadding>
          {alerts.map((alert, index) => {
            const assigneeName = resolveAssigneeName(alert.assigned_to, roster);
            return (
              <ListItem
                key={alert.alert_id}
                alignItems="flex-start"
                divider={index < alerts.length - 1}
                sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}
              >
                <Stack spacing={1.5} sx={{ minWidth: 0, width: "100%" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    sx={{
                      alignItems: { md: "flex-start" },
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="h2"
                        variant="subtitle1"
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          flexWrap: "wrap",
                          fontWeight: 700,
                          gap: 1,
                        }}
                      >
                        {alert.title}
                        <Chip
                          component="span"
                          size="small"
                          color={SEVERITY_COLOR[alert.severity]}
                          label={alert.severity}
                        />
                        <Chip
                          component="span"
                          size="small"
                          variant="outlined"
                          label={alert.status}
                        />
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {humanizeAlertType(alert.alert_type)}
                      </Typography>
                    </Box>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{ flexShrink: 0 }}
                    >
                      {formatDateTime(alert.created_at)}
                    </Typography>
                  </Stack>

                  {alert.body ? (
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 880, whiteSpace: "pre-wrap" }}
                    >
                      {alert.body}
                    </Typography>
                  ) : null}

                  {alert.assigned_to ? (
                    <Typography color="text.secondary" variant="body2">
                      Assigned to{" "}
                      <Box
                        component="span"
                        sx={{ color: "text.primary", fontWeight: 600 }}
                      >
                        {assigneeName ?? "Assigned"}
                      </Box>
                    </Typography>
                  ) : null}

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    {alert.status === "open" ? (
                      <Button
                        type="button"
                        size="small"
                        variant="contained"
                        aria-label={`Acknowledge ${alert.title}`}
                        disabled={locked}
                        onClick={() => onAcknowledge(alert)}
                      >
                        Acknowledge
                      </Button>
                    ) : null}

                    {alert.status === "open" ||
                    alert.status === "acknowledged" ? (
                      <>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          aria-label={`Assign ${alert.title}`}
                          disabled={locked}
                          onClick={() => onAssign(alert)}
                        >
                          Assign
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          aria-label={`Mute ${alert.title}`}
                          disabled={locked}
                          onClick={() => onMute(alert)}
                        >
                          Mute
                        </Button>
                      </>
                    ) : null}

                    {alert.status === "muted" ? (
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        aria-label={`Unmute ${alert.title}`}
                        disabled={locked}
                        onClick={() => onUnmute(alert)}
                      >
                        Unmute
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {total !== undefined && total > alerts.length ? (
        <Stack spacing={0.25}>
          <Typography color="text.secondary" variant="caption">
            Showing {alerts.length} of {total} alerts.
          </Typography>
          <Typography color="text.secondary" variant="caption">
            The newest loaded alerts are shown; filters can narrow the queue.
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
